import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, Trash2, Plus, Pencil, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { IntakeFormPreview } from './IntakeFormPreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUESTION_TYPES = ['text', 'textarea', 'number', 'date', 'email', 'phone', 'select', 'radio', 'checkboxes', 'yesno'];
const CHOICE = ['select', 'radio', 'checkboxes'];
const newId = () => `q-${Math.random().toString(36).slice(2, 10)}`;

const BlockEditor = ({ block, onChange }) => {
  const { t } = useTranslation();
  const set = (k, v) => onChange({ ...block, [k]: v });
  const isQuestion = QUESTION_TYPES.includes(block.type);
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {isQuestion && (
        <Select value={block.type} onValueChange={(v) => set('type', v)}>
          <SelectTrigger className="h-8 text-xs sm:col-span-2" data-testid={`tpl-block-type-${block.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{QUESTION_TYPES.map((q) => <SelectItem key={q} value={q}>{t(`intakeTemplates.types.${q}`)}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <Input className="h-8 text-sm" placeholder={t('intakeTemplates.caption')} value={block.label || ''}
        onChange={(e) => set('label', e.target.value)} data-testid={`tpl-block-label-${block.id}`} />
      <Input className="h-8 text-sm" placeholder={t('intakeTemplates.captionPl')} value={block.label_pl || ''}
        onChange={(e) => set('label_pl', e.target.value)} data-testid={`tpl-block-label-pl-${block.id}`} />
      <Input className="h-8 text-sm" placeholder={t('intakeTemplates.help')} value={block.help || ''}
        onChange={(e) => set('help', e.target.value)} data-testid={`tpl-block-help-${block.id}`} />
      <Input className="h-8 text-sm" placeholder={t('intakeTemplates.helpPl')} value={block.help_pl || ''}
        onChange={(e) => set('help_pl', e.target.value)} data-testid={`tpl-block-help-pl-${block.id}`} />
      {CHOICE.includes(block.type) && (
        <Textarea rows={3} className="text-sm sm:col-span-2" placeholder={t('intakeTemplates.optionsHint')}
          value={(block.options || []).join('\n')} onChange={(e) => set('options', e.target.value.split('\n'))}
          data-testid={`tpl-block-options-${block.id}`} />
      )}
      {isQuestion && (
        <label className="flex items-center gap-2 text-xs">
          <Checkbox checked={!!block.required} onCheckedChange={(v) => set('required', !!v)} data-testid={`tpl-block-required-${block.id}`} />
          {t('intakeTemplates.required')}
        </label>
      )}
    </div>
  );
};

const BlockRow = ({ block, index, count, onChange, onMove, onRemove }) => {
  const { t } = useTranslation();
  const title = block.type === 'builtin' ? t(`intakeTemplates.builtin.${block.key}`) : t(`intakeTemplates.types.${block.type}`);
  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid={`tpl-block-${block.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Badge variant={block.type === 'builtin' ? 'secondary' : 'outline'} className="shrink-0">{title}</Badge>
          {block.type === 'builtin' && <span className="truncate text-xs text-slate-500">{t('intakeTemplates.builtinHint')}</span>}
        </div>
        <div className="flex shrink-0 gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={index === 0} onClick={() => onMove(-1)} data-testid={`tpl-block-up-${block.id}`}><ArrowUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={index === count - 1} onClick={() => onMove(1)} data-testid={`tpl-block-down-${block.id}`}><ArrowDown className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={onRemove} data-testid={`tpl-block-remove-${block.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {block.type !== 'builtin' && <BlockEditor block={block} onChange={onChange} />}
    </li>
  );
};

const AddBlockBar = ({ blocks, builtinKeys, onAdd }) => {
  const { t } = useTranslation();
  const missing = builtinKeys.filter((k) => !blocks.some((b) => b.type === 'builtin' && b.key === k));
  return (
    <div className="flex flex-wrap gap-2" data-testid="tpl-add-bar">
      <Button size="sm" variant="outline" onClick={() => onAdd({ type: 'text', label: '', options: [] })} data-testid="tpl-add-question"><Plus className="h-3.5 w-3.5 mr-1" />{t('intakeTemplates.addQuestion')}</Button>
      <Button size="sm" variant="outline" onClick={() => onAdd({ type: 'heading', label: '' })} data-testid="tpl-add-heading"><Plus className="h-3.5 w-3.5 mr-1" />{t('intakeTemplates.addHeading')}</Button>
      <Button size="sm" variant="outline" onClick={() => onAdd({ type: 'caption', label: '' })} data-testid="tpl-add-caption"><Plus className="h-3.5 w-3.5 mr-1" />{t('intakeTemplates.addCaption')}</Button>
      {missing.length > 0 && (
        <Select value="" onValueChange={(k) => onAdd({ type: 'builtin', key: k })}>
          <SelectTrigger className="h-8 w-56 max-w-full text-xs" data-testid="tpl-add-builtin"><SelectValue placeholder={t('intakeTemplates.addBuiltin')} /></SelectTrigger>
          <SelectContent>{missing.map((k) => <SelectItem key={k} value={k} data-testid={`tpl-add-builtin-${k}`}>{t(`intakeTemplates.builtin.${k}`)}</SelectItem>)}</SelectContent>
        </Select>
      )}
    </div>
  );
};

const TemplateDialog = ({ draft, setDraft, builtinKeys, onSave, saving }) => {
  const { t } = useTranslation();
  if (!draft) return null;
  const blocks = draft.blocks || [];
  const setBlocks = (b) => setDraft({ ...draft, blocks: b });
  const move = (i, d) => { const b = [...blocks]; [b[i], b[i + d]] = [b[i + d], b[i]]; setBlocks(b); };
  return (
    <Dialog open onOpenChange={(o) => !o && setDraft(null)}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="tpl-editor-dialog">
        <DialogHeader><DialogTitle>{draft.id ? t('intakeTemplates.edit') : t('intakeTemplates.new')}</DialogTitle></DialogHeader>
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
        <div className="space-y-3 min-w-0">
          <Input value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder={t('intakeTemplates.name')} data-testid="tpl-name-input" />
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={!!draft.is_default} onCheckedChange={(v) => setDraft({ ...draft, is_default: !!v })} data-testid="tpl-default-checkbox" />
            {t('intakeTemplates.makeDefault')}
          </label>
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-600" data-testid="tpl-core-fields">
            {t('intakeTemplates.coreFields')}
          </div>
          <ul className="space-y-2">
            {blocks.map((b, i) => (
              <BlockRow key={b.id} block={b} index={i} count={blocks.length}
                onChange={(nb) => setBlocks(blocks.map((x) => (x.id === b.id ? nb : x)))}
                onMove={(d) => move(i, d)} onRemove={() => setBlocks(blocks.filter((x) => x.id !== b.id))} />
            ))}
          </ul>
          <AddBlockBar blocks={blocks} builtinKeys={builtinKeys} onAdd={(b) => setBlocks([...blocks, { id: newId(), ...b }])} />
        </div>
        <div className="min-w-0 lg:sticky lg:top-0 lg:self-start lg:max-h-[75vh] lg:overflow-y-auto">
          <IntakeFormPreview blocks={blocks} />
        </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDraft(null)}>{t('common.cancel')}</Button>
          <Button onClick={onSave} disabled={saving} data-testid="tpl-save-btn">{t('common.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Manage intake form templates (built-in sections + custom questions). */
export const IntakeTemplatesManager = ({ canShare, onChanged }) => {
  const { t } = useTranslation();
  const [data, setData] = useState({ items: [], standard_blocks: [], builtin_keys: [] });
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/candidate-intake-templates`); setData(r.data); } catch { /* no access */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { name: draft.name || '', blocks: draft.blocks, is_default: !!draft.is_default };
      if (draft.id) await axios.put(`${API}/candidate-intake-templates/${draft.id}`, body);
      else await axios.post(`${API}/candidate-intake-templates`, body);
      toast.success(t('intakeTemplates.saved'));
      setDraft(null); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || t('intakeTemplates.saveFailed')); }
    finally { setSaving(false); }
  };
  const remove = async (tpl) => {
    if (!window.confirm(t('intakeTemplates.confirmDelete', { name: tpl.name }))) return;
    try { await axios.delete(`${API}/candidate-intake-templates/${tpl.id}`); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || t('intakeTemplates.saveFailed')); }
  };
  const duplicate = (tpl) => setDraft({ name: `${tpl.name} (2)`, blocks: tpl.blocks.map((b) => ({ ...b, id: b.type === 'builtin' ? b.id : newId() })), is_default: false });

  return (
    <div className="space-y-3" data-testid="intake-templates-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800">{t('intakeTemplates.title')}</p>
        {canShare && (
          <Button size="sm" onClick={() => setDraft({ name: '', blocks: data.standard_blocks, is_default: false })} data-testid="tpl-new-btn">
            <Plus className="h-3.5 w-3.5 mr-1" />{t('intakeTemplates.new')}
          </Button>
        )}
      </div>
      {data.items.length === 0 && <p className="text-xs text-slate-500" data-testid="tpl-empty">{t('intakeTemplates.empty')}</p>}
      <ul className="space-y-2">
        {data.items.map((tpl) => (
          <li key={tpl.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-2" data-testid={`tpl-row-${tpl.id}`}>
            <span className="flex min-w-0 items-center gap-2 text-sm">
              {tpl.is_default && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-500" />}
              <span className="truncate font-medium">{tpl.name}</span>
              <span className="text-xs text-slate-500">{t('intakeTemplates.blockCount', { count: tpl.blocks.length })}</span>
            </span>
            {canShare && (
              <span className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setDraft(tpl)} data-testid={`tpl-edit-${tpl.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => duplicate(tpl)} data-testid={`tpl-duplicate-${tpl.id}`}>{t('intakeTemplates.duplicate')}</Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(tpl)} data-testid={`tpl-delete-${tpl.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </span>
            )}
          </li>
        ))}
      </ul>
      <TemplateDialog draft={draft} setDraft={setDraft} builtinKeys={data.builtin_keys} onSave={save} saving={saving} />
    </div>
  );
};

export const useIntakeTemplates = (refreshKey) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/candidate-intake-templates`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [refreshKey]);
  return items;
};

export default IntakeTemplatesManager;
